/**
 * User-applied finishes per (project, sku, room) — persisted to
 * localStorage. The actual Material node lives in the project graph
 * (kind: "Material"); this module just records which one a designer
 * picked for each placement. The merge step writes the chosen material
 * label into the SKU's attrs as `finish_${roomId}` so BOM, agent
 * suggestions, and the why-engine can read it.
 *
 * Parallel to lib/userPlacements.ts. Same per-project key pattern,
 * same write-through to graph attrs in mergeProjectLayers.
 */
import type { ProjectGraph } from "../types/ontology";

const KEY_PREFIX = "foyrmind.userFinishes.";

export interface AppliedFinish {
  readonly skuId: string;
  readonly roomId: string;
  /** Material node id from the project graph (kind === "Material"). */
  readonly materialId: string;
  readonly appliedAt: string;
}

function key(projectId: string): string { return KEY_PREFIX + projectId; }

export function readFinishes(projectId: string): AppliedFinish[] {
  try {
    const raw = window.localStorage.getItem(key(projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is AppliedFinish =>
      Boolean(p) && typeof p === "object"
      && typeof (p as AppliedFinish).skuId === "string"
      && typeof (p as AppliedFinish).roomId === "string"
      && typeof (p as AppliedFinish).materialId === "string",
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userFinishes] read failed", err);
    return [];
  }
}

export function setFinish(projectId: string, skuId: string, roomId: string, materialId: string): void {
  try {
    const current = readFinishes(projectId).filter((f) => !(f.skuId === skuId && f.roomId === roomId));
    const next: AppliedFinish[] = [...current, { skuId, roomId, materialId, appliedAt: new Date().toISOString() }];
    window.localStorage.setItem(key(projectId), JSON.stringify(next));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userFinishes] write failed", err);
  }
}

export function clearFinish(projectId: string, skuId: string, roomId: string): void {
  try {
    const next = readFinishes(projectId).filter((f) => !(f.skuId === skuId && f.roomId === roomId));
    window.localStorage.setItem(key(projectId), JSON.stringify(next));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userFinishes] clear failed", err);
  }
}

/** Merge applied finishes into the graph as SKU attrs so renderers,
 *  BOM, and agent suggestions read them uniformly. Writes:
 *    - finishMaterialId_${roomId} = "<material-id>"
 *    - finishLabel_${roomId}      = "<material-label>"
 *  Materials missing from the graph are dropped silently (they were
 *  deleted between save and now).
 */
export function mergeUserFinishes(graph: ProjectGraph, projectId: string): ProjectGraph {
  const finishes = readFinishes(projectId);
  if (finishes.length === 0) return graph;
  const materialById = new Map(
    graph.nodes.filter((n) => n.kind === "Material").map((n) => [n.id, n] as const),
  );
  const skuFinishes = new Map<string, Array<{ roomId: string; materialId: string; label: string }>>();
  for (const f of finishes) {
    const mat = materialById.get(f.materialId);
    if (!mat) continue;
    const arr = skuFinishes.get(f.skuId) ?? [];
    arr.push({ roomId: f.roomId, materialId: f.materialId, label: mat.label });
    skuFinishes.set(f.skuId, arr);
  }
  if (skuFinishes.size === 0) return graph;
  const nodes = graph.nodes.map((n) => {
    if (n.kind !== "SKU") return n;
    const list = skuFinishes.get(n.id);
    if (!list || list.length === 0) return n;
    const overrides: Record<string, string> = {};
    for (const f of list) {
      overrides[`finishMaterialId_${f.roomId}`] = f.materialId;
      overrides[`finishLabel_${f.roomId}`] = f.label;
    }
    return { ...n, attrs: { ...n.attrs, ...overrides } };
  });
  return { ...graph, nodes };
}
